/**
 * Os dados do cadastro, tela a tela, até virarem `POST /api/auth/register`.
 *
 * Ficam em `sessionStorage` (não em memória, como no mobile) porque na web
 * um recarregamento no meio do fluxo é comum — e recomeçar do onboarding por
 * causa de um F5 seria pior que o mobile, não fiel a ele. Some ao fechar a
 * aba, que é o tempo de vida certo para dado pessoal ainda não enviado.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { RegisterData } from '@eva/domain';

const CHAVE = 'eva_cadastro';

function ler(): RegisterData {
  try {
    const cru = sessionStorage.getItem(CHAVE);
    return cru ? (JSON.parse(cru) as RegisterData) : {};
  } catch {
    return {};
  }
}

function gravar(dados: RegisterData): void {
  try {
    sessionStorage.setItem(CHAVE, JSON.stringify(dados));
  } catch {
    /* sem storage: o fluxo segue só em memória */
  }
}

/**
 * Começa um cadastro pelo link de uma empresa (A062). Fora do provider
 * porque a página do link vem antes do fluxo; recomeça do zero de propósito:
 * um preenchimento antigo nesta aba não deve herdar a empresa nem o contrário.
 */
export function comecarCadastroPelaEmpresa(empresa: NonNullable<RegisterData['empresa']>): void {
  gravar({ empresa });
}

/** Para quem está fora do provider (a entrada na sessão, depois do cadastro). */
export function limparDadosDeCadastro(): void {
  try {
    sessionStorage.removeItem(CHAVE);
  } catch {
    /* idem */
  }
}

export interface RegisterValue {
  registerData: RegisterData;
  /**
   * A senha digitada em "Criar conta" (A062, fase C). Só em memória, nunca no
   * `sessionStorage`: um recarregamento no meio do fluxo pede a senha de novo,
   * e é o preço certo para não deixar senha em storage.
   */
  senha: string;
  setSenha: (s: string) => void;
  updateRegisterData: (parcial: Partial<RegisterData>) => void;
  clearRegisterData: () => void;
  /** Zera as respostas, mantendo o e-mail confirmado e o comprovante. */
  reiniciarRespostas: () => void;
}

const Ctx = createContext<RegisterValue | null>(null);

export function RegisterProvider({ children }: { children: ReactNode }) {
  const [registerData, setRegisterData] = useState<RegisterData>(ler);
  const [senha, setSenha] = useState('');

  const updateRegisterData = useCallback((parcial: Partial<RegisterData>) => {
    setRegisterData((atual) => {
      const novo = { ...atual, ...parcial };
      gravar(novo);
      return novo;
    });
  }, []);

  const clearRegisterData = useCallback(() => {
    limparDadosDeCadastro();
    setRegisterData({});
  }, []);

  /**
   * Zera as respostas do fluxo, MAS preserva o e-mail confirmado e o
   * comprovante.
   *
   * O onboarding limpava tudo ao montar, porque era o começo do cadastro.
   * Desde a A029 ele não é mais: o e-mail vem antes, e apagá-lo ali fazia a
   * conta nascer sem `emailVerified` — a pessoa confirmava o código e caía no
   * OTP de novo no fim.
   */
  const reiniciarRespostas = useCallback(() => {
    setRegisterData((atual) => {
      const novo: RegisterData = {};
      if (atual.email) novo.email = atual.email;
      if (atual.registrationToken) novo.registrationToken = atual.registrationToken;
      // A conversão (A032) precisa sobreviver ao reinício pela mesma razão do
      // comprovante: ela foi decidida ANTES do onboarding, e perdê-la aqui
      // faria a tela final pedir uma senha nova para quem já tem uma.
      if (atual.conversao) novo.conversao = atual.conversao;
      // A empresa do link (A062) também: ela foi escolhida antes do e-mail.
      if (atual.empresa) novo.empresa = atual.empresa;
      gravar(novo);
      return novo;
    });
  }, []);

  const value = useMemo(
    () => ({ registerData, updateRegisterData, clearRegisterData, reiniciarRespostas, senha, setSenha }),
    [registerData, updateRegisterData, clearRegisterData, reiniciarRespostas, senha]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useRegister(): RegisterValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useRegister precisa de <RegisterProvider>');
  return v;
}
